import { Banner, BlockStack, Box, Button, Card, ChoiceList, Icon, InlineStack, Layout, Page, Pagination, Popover, Text } from '@shopify/polaris';
import { ChevronDownIcon, ChevronUpIcon, RefreshIcon } from '@shopify/polaris-icons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getApiErrorMessage } from '../../app/api/error';
import agent, { FitmentImportHistoryItem, ImportHistoryStatus } from '../../app/api/agent';
import ImportHistoryCards from './ImportHistoryCards';

const pageSize = 20;

type ImportHistoryView = Extract<ImportHistoryStatus, 'active' | 'archived'>;

const historyViewOptions = [
    { value: 'active' as ImportHistoryView, label: 'Active' },
    { value: 'archived' as ImportHistoryView, label: 'Archived' },
];

const statusFilterButtonStyle: React.CSSProperties = {
    appearance: 'none',
    alignItems: 'center',
    background: 'transparent',
    border: 0,
    color: '#202223',
    cursor: 'pointer',
    display: 'inline-flex',
    font: 'inherit',
    gap: 6,
    minHeight: 32,
    padding: '4px 2px',
};

const statusFilterLabelStyle: React.CSSProperties = {
    fontSize: 14,
    lineHeight: '20px',
};

const statusFilterIconStyle: React.CSSProperties = {
    height: 16,
    width: 16,
};

const ImportHistoryPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const historyKind = searchParams.get('view') === 'exports' ? 'exports' : 'imports';
    const historyNoun = historyKind === 'exports' ? 'export' : 'import';
    const [history, setHistory] = useState<FitmentImportHistoryItem[]>([]);
    const historyRequest = useRef(0);
    const reloadHistory = useRef<() => Promise<void>>(async () => {});
    const [historyViews, setHistoryViews] = useState<ImportHistoryView[]>(['active']);
    const [statusFilterOpen, setStatusFilterOpen] = useState(false);
    const [historyError, setHistoryError] = useState<string | undefined>();
    const [historyErrorTitle, setHistoryErrorTitle] = useState<string | undefined>();
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [archivingIds, setArchivingIds] = useState<Record<number, boolean>>({});
    const [downloadingIds, setDownloadingIds] = useState<Record<number, boolean>>({});
    const [downloadingReviewIds, setDownloadingReviewIds] = useState<Record<number, boolean>>({});
    const [page, setPage] = useState(1);

    const historyStatus = useMemo<ImportHistoryStatus>(() => {
        const includesActive = historyViews.includes('active');
        const includesArchived = historyViews.includes('archived');

        if (includesActive && includesArchived) {
            return 'all';
        }

        return includesArchived ? 'archived' : 'active';
    }, [historyViews]);

    const loadHistory = useCallback(async () => {
        const request = ++historyRequest.current;
        try {
            setIsLoadingHistory(true);
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            // One extra row indicates whether another page exists, without counting every run.
            const result = await agent.Imports.history(historyStatus, {
                kind: historyKind,
                offset: (page - 1) * pageSize,
                limit: pageSize + 1,
            });
            if (request !== historyRequest.current) return;
            setHistory(result);
            if (result.length === 0 && page > 1) setPage((current) => current - 1);
        } catch (error: any) {
            const message = await getApiErrorMessage(error, `Failed to load ${historyNoun} history.`);
            if (request === historyRequest.current) setHistoryError(message);
        } finally {
            if (request === historyRequest.current) setIsLoadingHistory(false);
        }
    }, [historyKind, historyNoun, historyStatus, page]);

    useEffect(() => { setPage(1); }, [historyKind]);

    useEffect(() => {
        reloadHistory.current = loadHistory;
        setHistory([]);
        loadHistory();
        return () => {
            historyRequest.current++;
            reloadHistory.current = async () => {};
        };
    }, [loadHistory]);

    const hasNextPage = history.length > pageSize;
    const currentItems = useMemo(() => history.slice(0, pageSize), [history]);

    const historyTitle = historyStatus === 'all'
        ? `All ${historyKind}`
        : historyStatus === 'archived'
            ? `Archived ${historyKind}`
            : `Active ${historyKind}`;
    const emptyHistoryText = historyStatus === 'archived'
        ? `No archived ${historyKind}.`
        : historyStatus === 'all'
            ? `No ${historyNoun} history yet.`
            : `No active ${historyKind}.`;

    const handleHistoryViewsChange = useCallback((value: string[]) => {
        if (value.length === 0) {
            return;
        }

        setHistoryViews(value as ImportHistoryView[]);
        setPage(1);
    }, []);

    const toggleStatusFilter = useCallback(() => {
        setStatusFilterOpen((current) => !current);
    }, []);

    const closeStatusFilter = useCallback(() => {
        setStatusFilterOpen(false);
    }, []);

    const handleArchive = useCallback(async (queueId: number) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setArchivingIds((prev) => ({ ...prev, [queueId]: true }));
            await agent.Imports.archive(queueId);
            await reloadHistory.current();
        } catch (error: any) {
            setHistoryErrorTitle('Unable to archive history item');
            setHistoryError(await getApiErrorMessage(error, 'Failed to archive history item.'));
        } finally {
            setArchivingIds((prev) => ({ ...prev, [queueId]: false }));
        }
    }, []);

    const handleDownload = useCallback(async (item: FitmentImportHistoryItem) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setDownloadingIds((prev) => ({ ...prev, [item.queueId]: true }));
            const response = await agent.Imports.download(item.queueId);
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = item.originalFileName || `${historyNoun}-${item.queueId}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            setHistoryErrorTitle(`Unable to download ${historyNoun} file`);
            setHistoryError(await getApiErrorMessage(error, `Failed to download ${historyNoun} file.`));
        } finally {
            setDownloadingIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, [historyNoun]);

    const handleDownloadReview = useCallback(async (item: FitmentImportHistoryItem) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setDownloadingReviewIds((prev) => ({ ...prev, [item.queueId]: true }));
            const response = await agent.Imports.downloadReview(item.queueId);
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            const baseName = (item.originalFileName || `import-${item.queueId}`).replace(/\.[^/.]+$/, '');
            link.href = blobUrl;
            link.download = `${baseName}-review.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            setHistoryErrorTitle('Unable to download review file');
            setHistoryError(await getApiErrorMessage(error, 'Failed to download import review file.'));
        } finally {
            setDownloadingReviewIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, []);

    return (
        <Page
            title={historyKind === 'exports' ? 'Export history' : 'Import history'}
            backAction={{ content: historyKind === 'exports' ? 'Exports' : 'Imports',
                onAction: () => navigate(historyKind === 'exports' ? '/imports?view=exports' : '/imports') }}
        >
            <Layout>
                <Layout.Section>
                    <Card>
                        <BlockStack gap="300">
                            <InlineStack align="space-between" blockAlign="center" gap="300">
                                <InlineStack gap="300" blockAlign="center" wrap>
                                    <Text as="h2" variant="headingMd">{historyTitle}</Text>
                                    <Popover
                                        active={statusFilterOpen}
                                        activator={(
                                            <button
                                                type="button"
                                                aria-expanded={statusFilterOpen}
                                                aria-haspopup="true"
                                                onClick={toggleStatusFilter}
                                                style={statusFilterButtonStyle}
                                            >
                                                <span style={statusFilterLabelStyle}>Status</span>
                                                <span style={statusFilterIconStyle}>
                                                    <Icon source={statusFilterOpen ? ChevronUpIcon : ChevronDownIcon} />
                                                </span>
                                            </button>
                                        )}
                                        onClose={closeStatusFilter}
                                    >
                                        <Box padding="300" minWidth="180px">
                                            <ChoiceList
                                                title={historyKind === 'exports' ? 'Export status' : 'Import status'}
                                                titleHidden
                                                allowMultiple
                                                choices={historyViewOptions}
                                                selected={historyViews}
                                                onChange={handleHistoryViewsChange}
                                            />
                                        </Box>
                                    </Popover>
                                </InlineStack>
                                <Button variant="tertiary" icon={RefreshIcon} onClick={loadHistory} loading={isLoadingHistory}>Refresh</Button>
                            </InlineStack>

                            {historyError && (
                                <Banner tone="critical" title={historyErrorTitle || `Unable to load ${historyNoun} history`}>
                                    <p>{historyError}</p>
                                </Banner>
                            )}

                            {history.length === 0 && !isLoadingHistory ? (
                                <Text as="p" tone="subdued">{emptyHistoryText}</Text>
                            ) : (
                                <>
                                    <ImportHistoryCards
                                        items={currentItems}
                                        archivingIds={archivingIds}
                                        downloadingIds={downloadingIds}
                                        downloadingReviewIds={downloadingReviewIds}
                                        onArchive={handleArchive}
                                        onDownload={handleDownload}
                                        onDownloadReview={handleDownloadReview}
                                    />
                                    <Box paddingBlockStart="200">
                                        <InlineStack align="center">
                                            <Pagination
                                                hasPrevious={!isLoadingHistory && page > 1}
                                                onPrevious={() => setPage((prev) => Math.max(1, prev - 1))}
                                                hasNext={!isLoadingHistory && hasNextPage}
                                                onNext={() => setPage((prev) => prev + 1)}
                                            />
                                        </InlineStack>
                                    </Box>
                                </>
                            )}
                        </BlockStack>
                    </Card>
                </Layout.Section>
            </Layout>
        </Page>
    );
};

export default ImportHistoryPage;
